import { flatten, pick } from 'lodash';
import { isAxiosError } from 'axios';
import { ApiError, AppError, ErrorCode } from '@/types/ErrorCodes';
import { formatDistance } from 'date-fns';
import { BaseEntity } from '@/api/EntityApi';
import {
	CreatorActionType,
	FilterOperator,
	Language,
	RelationAction,
	RelationType,
	Translations,
} from '@/types/types';
import { pl } from 'date-fns/locale';
import { AttributeViewType } from '@/api/AttributeApi';
import { CompanyTransportType } from '@/api/CompanyApi';
import { InvestmentStatus } from '@/api/InvestmentApi';
import { MontageType } from '@/api/ProductTypeApi';
import { DatesRangeValue } from '@mantine/dates';
import { ArticleQuantityType } from '@/api/ArticleApi';
import {
	QuantityConfigCondition,
	QuantityConfigType,
} from '@/api/ComponentVariationApi';
import APP_CONFIG from '@/configs/appConfig';
import LANGUAGES from '@/configs/languages';
import moment from 'moment';

export const priceFormatter = new Intl.NumberFormat('PL-pl', {
	style: 'currency',
	currency: 'PLN',
	currencyDisplay: 'code',
});

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

export function randomDate(start: Date, end: Date) {
	return moment(
		start.getTime() + Math.random() * (end.getTime() - start.getTime())
	).toDate();
}

export function formatDateForApi(date: moment.MomentInput) {
	return moment(date).format('YYYY-MM-DD');
}

export function formatDateForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('DD/MM/YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

export function sortByDate(
	dateA: moment.MomentInput,
	dateB: moment.MomentInput,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function sortByName(
	nameA = '',
	nameB = '',
	order: 'asc' | 'desc' = 'asc'
) {
	return order === 'asc'
		? nameA.localeCompare(nameB)
		: nameB.localeCompare(nameA);
}

export function incrementDate(date: moment.MomentInput) {
	return moment(date).add(1, 'day').toDate();
}

export function decrementDate(date: moment.MomentInput) {
	return moment(date).subtract(1, 'day').toDate();
}

export function handleUnload(e: BeforeUnloadEvent) {
	e.preventDefault();
	e.returnValue = '';
}

export const ID_REGEX =
	/[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[89ab][a-f0-9]{3}-?[a-f0-9]{12}/g;

export function getIdsFromText(text: string) {
	return [...new Set(flatten([...text.matchAll(ID_REGEX)]))];
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export function priceToFixed(price: number) {
	return price.toFixed(2);
}

export const formatPrice = (value = 0, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('pl', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export const isAppError = (error: unknown): error is AppError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as AppError).message
	);
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export const fontColorForBackground = (backgroundColorHex = '#ffffff') => {
	return [
		0.299 * parseInt(backgroundColorHex.slice(1, 3), 16),
		0.587 * parseInt(backgroundColorHex.slice(3, 5), 16),
		0.114 * parseInt(backgroundColorHex.slice(5, 7), 16),
	].reduce((a, b) => a + b) /
		255 >
		0.65
		? '#495057'
		: '#ffffff';
};

export const formatFullName = (firstName?: string, lastName?: string) => {
	if (firstName && lastName) return `${firstName} ${lastName}`;
	if (!firstName && !lastName) return `-`;
	return firstName || lastName || '-';
};

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatDateFromNow(date: Date | string) {
	return formatDistance(date, new Date(), {
		locale: pl,
		includeSeconds: true,
		addSuffix: true,
	});
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function fileSizeToMB(size: number | string) {
	return Math.round((Number(size) / 1000000 + Number.EPSILON) * 100) / 100;
}

export function extractId<TData extends BaseEntity>(entity: TData) {
	return pick(entity, 'id');
}

export function getAttributeViewTypeName(viewType: AttributeViewType) {
	switch (viewType) {
		case AttributeViewType.TILES:
			return 'Kafelki';
		case AttributeViewType.CHIP:
			return 'Chip';
	}
}

export function getEmptyTranslationObject<K extends string>(
	keys: K[]
): Translations<Record<K, string>> {
	const translations = {} as Translations<Record<K, string>>;

	let language: Language;
	for (language in LANGUAGES) {
		if (language === APP_CONFIG.DEFAULT_LANGUAGE) continue;

		translations[language] = {} as Record<K, string>;

		for (const key of keys) {
			translations[language][key] = '';
		}
	}

	return translations;
}

export function mapCompanyTransportTypeName(
	companyTransportType: CompanyTransportType
) {
	switch (companyTransportType) {
		case CompanyTransportType.EXTERIOR:
			return 'Zewnętrzny';
		case CompanyTransportType.INTERIOR:
			return 'Wewnętrzny';
	}
}

export function getInvestmentStatusName(status: InvestmentStatus) {
	switch (status) {
		case InvestmentStatus.NOT_SENT:
			return 'Nie wysłane';
		case InvestmentStatus.SENT:
			return 'Wysłano';
		case InvestmentStatus.CANCELLED:
			return 'Cofnięte';
		case InvestmentStatus.IN_PROGRESS:
			return 'W realizacji';
		case InvestmentStatus.COMPLETED:
			return 'Zakończone';
	}
}

export function getInvestmentStatusColor(status: InvestmentStatus) {
	switch (status) {
		case InvestmentStatus.NOT_SENT:
			return 'primary';
		case InvestmentStatus.SENT:
			return 'info';
		case InvestmentStatus.CANCELLED:
			return 'warning';
		case InvestmentStatus.IN_PROGRESS:
			return 'pink';
		case InvestmentStatus.COMPLETED:
			return 'success';
	}
}

export function getRelationActionName(action: RelationAction) {
	switch (action) {
		case RelationAction.HIDE:
			return 'Ukryj';
		case RelationAction.SHOW:
			return 'Pokaż';
	}
}

export function getRelationTypeName(type: RelationType) {
	switch (type) {
		case RelationType.ATTRIBUTE_VALUE:
			return 'Wartość atrybutu';
		case RelationType.COMPONENT_VARIATION:
			return 'Wariant komponentu';
	}
}

export function mapCreatorActionTypeName(type: CreatorActionType) {
	switch (type) {
		case CreatorActionType.SET_SEGMENT_MIN_WIDTH:
			return 'Min. rekomendowana szerokość segmentu';
		case CreatorActionType.SET_SEGMENT_MAX_WIDTH:
			return 'Maks. rekomendowana szerokość segmentu';
		case CreatorActionType.WITH_MOSQUITO_NET:
			return 'Dostępność moskitiery';
		case CreatorActionType.WITH_ARMOR:
			return 'Z pancerzem';
	}
}

export function mapMontageTypeName(type: MontageType) {
	switch (type) {
		case MontageType.INSIDE:
			return 'Wnęka okienna';
		case MontageType.ON_WALL:
			return 'Rama okienna';
		case MontageType.BOTH:
			return 'Wnęka okienna i rama okienna';
	}
}

export function getQuantityConfigTypeName(type: QuantityConfigType) {
	switch (type) {
		case QuantityConfigType.PROJECT:
			return 'Dla całości';
		case QuantityConfigType.ROLLER_BOX_SEGMENT:
			return 'Dla segmentu rolety';
		case QuantityConfigType.MOSQUITO_NET_SEGMENT:
			return 'Dla segmentu moskitiery';
	}
}

export function getQuantityConfigConditionName(
	condition: QuantityConfigCondition | null
) {
	if (!condition) return '-';

	switch (condition) {
		case QuantityConfigCondition.AE_TO_AE:
			return 'AE do AE';
		case QuantityConfigCondition.AE_TO_AC:
			return 'AE i AC';
		case QuantityConfigCondition.AE_TO_AC_WIDE:
			return 'AE i AC szer.';
		case QuantityConfigCondition.AC_THIN_TO_AC_THIN:
			return 'AC wąs. do AC wąs.';
		case QuantityConfigCondition.AC_THIN_TO_AC_WIDE:
			return 'AC wąs. i AC szer.';
		case QuantityConfigCondition.AC_WIDE_TO_AC_WIDE:
			return 'AC szer. do AC szer.';
		case QuantityConfigCondition.AE_TO_MOSQUITO_NET_EDGE:
			return 'AE do moskitiera';
		case QuantityConfigCondition.AC_THIN_TO_MOSQUITO_NET_EDGE:
			return 'AC wąs. do moskitiera';
		case QuantityConfigCondition.AC_WIDE_TO_MOSQUITO_NET_EDGE:
			return 'AC szer. do moskitiera';
		case QuantityConfigCondition.AC_TO_MOSQUITO_NET_EDGE:
			return 'AC do moskitiera';
		case QuantityConfigCondition.MOSQUITO_NET_EDGE_TO_MOSQUITO_NET_EDGE:
			return 'moskitiera do moskitiery';
		case QuantityConfigCondition.ROLLER_BOX_HEIGHT:
			return 'Wysokość skrzynki';
		default:
			return 'Brak';
	}
}

export function getArticleQuantityTypeName(type: ArticleQuantityType) {
	switch (type) {
		case ArticleQuantityType.SUM:
			return 'Suma';
		case ArticleQuantityType.PACKAGES:
			return 'Paczki';
		case ArticleQuantityType.LENGTH:
			return 'Metry';
		case ArticleQuantityType.PACKAGE_AND_LENGTH:
			return 'Paczki i metry';
	}
}

export function getArticleQuantityTypeColor(type: ArticleQuantityType) {
	switch (type) {
		case ArticleQuantityType.SUM:
			return 'primary';
		case ArticleQuantityType.PACKAGES:
			return 'info';
		case ArticleQuantityType.LENGTH:
			return 'success';
		case ArticleQuantityType.PACKAGE_AND_LENGTH:
			return 'warning';
	}
}

export function formatRangeDataForAPIFilter(dateRange: DatesRangeValue) {
	const [from, to] = dateRange;

	if (!from && !to) return null;

	if (from && to)
		return `${FilterOperator.BTW}:${formatDateForApi(from)},${formatDateForApi(to)}`;

	if (from) return `${FilterOperator.GTE}:${formatDateForApi(from)}`;

	return `${FilterOperator.LTE}:${formatDateForApi(to)}`;
}
